import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { currentFiscalYear } from "@/lib/fiscal";
import { cn } from "@/lib/utils";
import {
  CAMPUS_METRICS,
  CopyToFYSelect,
  SectionHeader,
  savedFieldClass,
  useSaveFlash,
} from "./shared";

export function CampusTargetsSection({
  ownerId,
  fiscalYear,
  onDataChange,
  onRemove,
  readOnly = false,
}: {
  ownerId: string;
  fiscalYear: number;
  onDataChange: (hasData: boolean) => void;
  onRemove?: () => void;
  readOnly?: boolean;
}) {
  const currentFY = currentFiscalYear();
  const [leadsTarget, setLeadsTarget] = useState("");
  const [dealsTarget, setDealsTarget] = useState("");
  const [loading, setLoading] = useState(true);
  const { savedField, flashSaved } = useSaveFlash();
  const lastSavedRef = useRef<Record<string, string>>({});

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/targets?fy=${fiscalYear}`);
      if (res.ok) {
        const data = await res.json();
        const targets = data.targets as { metric: string; targetValue: number }[];
        const leads = targets.find((t) => t.metric === CAMPUS_METRICS.leads);
        const deals = targets.find((t) => t.metric === CAMPUS_METRICS.dealsWon);
        const leadsVal = leads ? String(leads.targetValue) : "";
        const dealsVal = deals ? String(deals.targetValue) : "";
        setLeadsTarget(leadsVal);
        setDealsTarget(dealsVal);
        lastSavedRef.current = {
          campus_er_leads: leadsVal,
          campus_er_deals_won: dealsVal,
        };
        onDataChange(leadsVal !== "" || dealsVal !== "");
      }
    } finally {
      setLoading(false);
    }
  }, [ownerId, fiscalYear, onDataChange]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  const saveMetric = useCallback(
    async (metric: string, value: string) => {
      if (value === (lastSavedRef.current[metric] ?? "")) return;

      const res = await fetch(`/api/owners/${ownerId}/targets`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fiscalYear,
          targets: [{ metric, targetValue: value === "" ? null : Number(value) }],
        }),
      });

      if (res.ok) {
        lastSavedRef.current[metric] = value;
        flashSaved(metric);
        const otherMetric =
          metric === CAMPUS_METRICS.leads ? CAMPUS_METRICS.dealsWon : CAMPUS_METRICS.leads;
        const otherValue = otherMetric === CAMPUS_METRICS.leads ? leadsTarget : dealsTarget;
        onDataChange(value !== "" || otherValue !== "");
      }
    },
    [ownerId, fiscalYear, flashSaved, leadsTarget, dealsTarget, onDataChange],
  );

  const leadsNum = Number(leadsTarget) || 0;
  const dealsNum = Number(dealsTarget) || 0;
  const conversionRate = leadsNum > 0 ? ((dealsNum / leadsNum) * 100).toFixed(1) : "—";

  async function copyToFY(targetFY: number) {
    const targets: { metric: string; targetValue: number | null }[] = [];
    if (leadsTarget !== "")
      targets.push({ metric: CAMPUS_METRICS.leads, targetValue: Number(leadsTarget) });
    if (dealsTarget !== "")
      targets.push({ metric: CAMPUS_METRICS.dealsWon, targetValue: Number(dealsTarget) });
    if (targets.length === 0) return;

    await fetch(`/api/owners/${ownerId}/targets`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear: targetFY, targets }),
    });
  }

  async function removeAll() {
    const targets = [
      { metric: CAMPUS_METRICS.leads, targetValue: null },
      { metric: CAMPUS_METRICS.dealsWon, targetValue: null },
    ];
    await fetch(`/api/owners/${ownerId}/targets`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear, targets }),
    });
    setLeadsTarget("");
    setDealsTarget("");
    lastSavedRef.current = {};
    onDataChange(false);
    onRemove?.();
  }

  const hasData = leadsTarget !== "" || dealsTarget !== "";

  return (
    <Card>
      <CardContent className="pt-5">
        <SectionHeader title="Campus Targets" onRemove={readOnly ? undefined : removeAll}>
          {!readOnly && hasData && (
            <CopyToFYSelect currentFY={currentFY} selectedFY={fiscalYear} onCopy={copyToFY} />
          )}
        </SectionHeader>

        <div className={cn("space-y-3", loading && "opacity-60")}>
          <div className="space-y-1.5">
            <Label className="text-sm">Campus ER Leads Generated</Label>
            <Input
              type="number"
              value={leadsTarget}
              onChange={(e) => setLeadsTarget(e.target.value)}
              onBlur={() => saveMetric(CAMPUS_METRICS.leads, leadsTarget)}
              placeholder="Enter target"
              disabled={readOnly}
              className={savedFieldClass(savedField, CAMPUS_METRICS.leads)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Campus ER Deals Won</Label>
            <Input
              type="number"
              value={dealsTarget}
              onChange={(e) => setDealsTarget(e.target.value)}
              onBlur={() => saveMetric(CAMPUS_METRICS.dealsWon, dealsTarget)}
              placeholder="Enter target"
              disabled={readOnly}
              className={savedFieldClass(savedField, CAMPUS_METRICS.dealsWon)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-muted-foreground">Targeted Conversion Rate</Label>
            <p className="text-lg font-bold tabular-nums py-1.5">
              {conversionRate === "—" ? "—" : `${conversionRate}%`}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
